import { useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import {
  analyzeMenuPhoto,
  createMenuProduct,
  listMenuProducts,
  patchMenuProduct,
  putMenuPhoto,
} from '../api/endpoints/menu';
import { createIdempotencyKeyHolder } from '../api/idempotency';
import type { MenuProduct, MenuProductProposal } from '../api/schemas';
import { ConfirmAction } from '../components/ConfirmAction';
import { EmptyState } from '../components/EmptyState';
import { ErrorBanner } from '../components/ErrorBanner';
import { FormField } from '../components/FormField';
import { LoadingState } from '../components/LoadingState';
import { PageHeader } from '../components/PageHeader';
import { formatMoneyCents } from '../lib/format';
import { parsePriceInput } from '../lib/money';
import { buildVisionImageUpload } from '../lib/photoPipeline';

const MENU_QUERY_KEY = ['menu-products'];

type Draft = { name: string; category: string; priceInput: string; selected: boolean };

const emptyDraft = (): Draft => ({ name: '', category: 'Platos', priceInput: '', selected: true });

/** The restaurant's sellable catalog. Proposals stay local until this owner keeps them. */
export function MenuPage() {
  const client = useQueryClient();
  const productsQuery = useQuery({ queryKey: MENU_QUERY_KEY, queryFn: () => listMenuProducts(true) });
  // The owner names their own categories, so the only honest list is the one
  // they have already used. No fixed taxonomy, no invented sections.
  const categories = usedCategories(productsQuery.data?.data);
  const [draft, setDraft] = useState(emptyDraft);
  const [proposals, setProposals] = useState<Draft[] | undefined>();
  const [photoError, setPhotoError] = useState<string | undefined>();
  const photoKey = useRef(createIdempotencyKeyHolder()).current;
  const createKey = useRef(createIdempotencyKeyHolder()).current;
  // One ledger key per proposed row. A partial batch retry presents the exact
  // same keys, so a committed first row cannot be duplicated while row two is
  // retried. These reset only when this proposal set is finished or abandoned.
  const proposalKeys = useRef<ReturnType<typeof createIdempotencyKeyHolder>[]>([]);
  const resetProposalKeys = () => {
    proposalKeys.current.forEach((holder) => holder.reset());
    proposalKeys.current = [];
  };

  const refresh = () => void client.invalidateQueries({ queryKey: MENU_QUERY_KEY });
  const createMutation = useMutation({
    mutationFn: (next: Draft) => createMenuProduct(toInput(next), createKey.ensure()),
    meta: { exito: 'Producto agregado' },
    onSuccess: () => { setDraft(emptyDraft()); refresh(); },
    onSettled: () => createKey.reset(),
  });
  const photoMutation = useMutation({
    mutationFn: async (file: File) => {
      const image = await buildVisionImageUpload(file);
      const uploaded = await putMenuPhoto(image, photoKey.ensure());
      return analyzeMenuPhoto(uploaded.objectKey);
    },
    onSuccess: (found) => {
      setPhotoError(undefined);
      resetProposalKeys();
      setProposals(found.map(toDraft));
    },
    onError: (error) => setPhotoError(photoMessage(error)),
    onSettled: () => photoKey.reset(),
  });
  const keepMutation = useMutation({
    mutationFn: async (items: Draft[]) => {
      for (const [index, item] of items.entries()) {
        if (!item.selected) continue;
        const holder = (proposalKeys.current[index] ??= createIdempotencyKeyHolder());
        await createMenuProduct(toInput(item), holder.ensure());
      }
    },
    meta: { exito: (items) => ((items as Draft[]).filter((item) => item.selected).length === 1 ? 'Producto agregado' : 'Productos agregados') },
    onSuccess: () => { resetProposalKeys(); setProposals(undefined); refresh(); },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    createMutation.mutate(draft);
  }

  return (
    <main>
      <PageHeader title="Menú" description="Los platos y bebidas que vendes." />
      <section className="section" aria-label="Foto del menú">
        <h2 className="section-label">Traer desde una foto</h2>
        <div className="card form-grid">
          <p className="row-sub">Revisa cada propuesta antes de agregarla. Nada se publica solo.</p>
          <FormField label="Foto del menú" htmlFor="menu-photo" hint="JPEG, PNG o WebP">
            <input
              id="menu-photo"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              disabled={photoMutation.isPending}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) photoMutation.mutate(file);
                event.currentTarget.value = '';
              }}
            />
          </FormField>
          {photoMutation.isPending ? <LoadingState label="Leyendo el menú…" /> : null}
          {photoError ? <ErrorBanner message={photoError} /> : null}
        </div>
      </section>

      {proposals ? (
        <ProposalReview
          proposals={proposals}
          categories={categories}
          pending={keepMutation.isPending}
          error={keepMutation.isError ? message(keepMutation.error) : undefined}
          onChange={setProposals}
          onKeep={() => keepMutation.mutate(proposals)}
          onDiscard={() => { resetProposalKeys(); setProposals(undefined); }}
        />
      ) : null}

      {productsQuery.isLoading ? <LoadingState label="Cargando el menú…" /> : null}
      {productsQuery.isError ? <ErrorBanner message={message(productsQuery.error)} /> : null}
      {productsQuery.data ? (
        productsQuery.data.data.length === 0 ? (
          <EmptyState title="Todavía no tienes productos en el menú." description="Agrega un plato o sube una foto para empezar." />
        ) : (
          <ul className="rows">
            {productsQuery.data.data.map((product) => <li key={product.id}><MenuRow product={product} /></li>)}
          </ul>
        )
      ) : null}

      <section className="section" aria-label="Agregar producto">
        <h2 className="section-label">Agregar producto</h2>
        <form className="card form-grid cols-2" onSubmit={submit}>
          <ProductFields draft={draft} onChange={setDraft} categories={categories} />
          {createMutation.isError ? <ErrorBanner message={message(createMutation.error)} /> : null}
          <button type="submit" className="btn" disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Agregando…' : 'Agregar al menú'}
          </button>
        </form>
      </section>
    </main>
  );
}

function ProposalReview({ proposals, categories, pending, error, onChange, onKeep, onDiscard }: {
  proposals: Draft[]; categories: string[]; pending: boolean; error?: string; onChange: (items: Draft[]) => void; onKeep: () => void; onDiscard: () => void;
}) {
  const update = (index: number, patch: Partial<Draft>) => onChange(proposals.map((item, i) => i === index ? { ...item, ...patch } : item));
  const selected = proposals.filter((item) => item.selected).length;
  return <section className="section" aria-label="Revisar propuestas">
    <h2 className="section-label">Revisar propuestas</h2>
    <div className="card form-grid">
      <p className="row-sub">Escoge lo que quieres conservar y corrige lo necesario.</p>
      {proposals.length === 0 ? <p className="row-sub">No encontramos productos legibles. Agrégalos manualmente abajo.</p> : proposals.map((item, index) => (
        <fieldset className="form-grid cols-2" key={index}>
          <legend className="sr-only">Propuesta {index + 1}</legend>
          <label className="span-2"><input type="checkbox" checked={item.selected} onChange={(event) => update(index, { selected: event.target.checked })} /> Conservar</label>
          <ProductFields draft={item} onChange={(patch) => update(index, patch)} categories={categories} id={`proposal-${index}`} />
        </fieldset>
      ))}
      {error ? <ErrorBanner message={error} /> : null}
      <span className="action-pair">
        <button type="button" className="btn" disabled={pending || selected === 0} onClick={onKeep}>{pending ? 'Agregando…' : `Agregar ${selected} al menú`}</button>
        <button type="button" className="btn small ghost" disabled={pending} onClick={onDiscard}>Descartar</button>
      </span>
    </div>
  </section>;
}

function ProductFields({ draft, onChange, categories = [], id = 'menu' }: { draft: Draft; onChange: (value: Draft) => void; categories?: string[]; id?: string }) {
  return <>
    <FormField label="Nombre" htmlFor={`${id}-name`} className="span-2"><input id={`${id}-name`} value={draft.name} onChange={(event) => onChange({ ...draft, name: event.target.value })} required /></FormField>
    <FormField label="Categoría" htmlFor={`${id}-category`}>
      <input id={`${id}-category`} value={draft.category} onChange={(event) => onChange({ ...draft, category: event.target.value })} required />
      {categories.length > 0 ? (
        <div className="chip-row" aria-label="Categorías que ya usas">
          {categories.map((category) => (
            <button key={category} type="button" className="chip" data-status={category === draft.category ? 'active' : undefined} onClick={() => onChange({ ...draft, category })}>
              {category}
            </button>
          ))}
        </div>
      ) : null}
    </FormField>
    <FormField label="Precio" htmlFor={`${id}-price`} hint="En dólares, por ejemplo 12.50"><input id={`${id}-price`} inputMode="decimal" placeholder="12.50" value={draft.priceInput} onChange={(event) => onChange({ ...draft, priceInput: event.target.value })} required /></FormField>
  </>;
}

function MenuRow({ product }: { product: MenuProduct }) {
  const client = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(toDraft(product));
  const key = useRef(createIdempotencyKeyHolder()).current;
  const mutation = useMutation({
    mutationFn: (input: Parameters<typeof patchMenuProduct>[1]) => patchMenuProduct(product.id, input, key.ensure()),
    onSuccess: () => { setEditing(false); void client.invalidateQueries({ queryKey: MENU_QUERY_KEY }); },
    onSettled: () => key.reset(),
  });
  return <div className="row">
    {editing ? <form className="form-grid cols-2" onSubmit={(event) => { event.preventDefault(); mutation.mutate(toInput(draft)); }}><ProductFields draft={draft} onChange={setDraft} id={`product-${product.id}`} /><span className="action-pair"><button className="btn small" disabled={mutation.isPending}>Guardar</button><button type="button" className="btn small ghost" onClick={() => { setDraft(toDraft(product)); setEditing(false); }}>Cancelar</button></span></form> : <><div className="grow"><p className="row-title">{product.name} {!product.isActive ? <span className="status" data-tone="off">Desactivado</span> : null}</p><p className="row-sub">{product.category} · {formatMoneyCents(product.priceCents)}</p></div><span className="action-pair"><button type="button" className="btn small ghost" onClick={() => setEditing(true)}>Editar</button><ConfirmAction label={product.isActive ? 'Desactivar' : 'Activar'} pending={mutation.isPending} onConfirm={() => mutation.mutate({ isActive: !product.isActive })} /></span></>}
    {mutation.isError ? <ErrorBanner message={message(mutation.error)} /> : null}
  </div>;
}

/** Distinct categories in use, in Panamanian collation order. */
function usedCategories(products: MenuProduct[] | undefined): string[] {
  return [...new Set((products ?? []).map((product) => product.category))].sort((a, b) =>
    a.localeCompare(b, 'es-PA'),
  );
}

function toDraft(item: MenuProductProposal | MenuProduct): Draft { return { name: item.name, category: item.category, priceInput: (item.priceCents / 100).toFixed(2), selected: true }; }
function toInput(draft: Draft) { const price = parsePriceInput(draft.priceInput); if (!draft.name.trim() || !draft.category.trim()) throw new Error('Revisa el nombre y la categoría.'); if (!price.ok || price.cents <= 0) throw new Error(price.ok ? 'El precio debe ser mayor que cero.' : price.message); return { name: draft.name.trim(), category: draft.category.trim(), priceCents: price.cents }; }
function message(error: unknown) { return error instanceof ApiError || error instanceof Error ? error.message : 'No pudimos completar eso ahora mismo.'; }
function photoMessage(error: unknown) { if (error instanceof ApiError && error.status === 503) return 'La lectura automática no está disponible ahora. Agrega los productos manualmente.'; return message(error); }
